import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import styled from 'styled-components';
import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { Button, Flex, Text } from '@mantine/core';
import { useKYCRequirementsController } from './useKYCRequirementsController.ts';
import { BorderedBox } from './common/BorderedBox.tsx';
import { HowTopProvideDocsModal } from '@dealstack/frontend/shared/components/HowToProvideDocs/HowTopProvideDocsModal.tsx';
import { KYCDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/KYCDocuments.tsx';
import { Navigate, Route } from 'react-router-dom';
import {
	MIP_CREATE_ROUTES,
	MIP_CREATE_SUB_ROUTES,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController.ts';
import { KYCDeclarations } from './KYCDeclarations/KYCDeclarations.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry.ts';
import { NoSaveStep } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';

interface Props {
	className?: string;
	mipCreateRoute: string;
}

const KYCRequirements = ({ mipCreateRoute, className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const {
		proofs,
		certification,
		isAnyCertificationRequired,
		onProofChange,
		onCertificationChange,
		isSaving,
		showHowToProvideDocsModal,
		setShowHowToProvideDocsModal,
	} = useKYCRequirementsController();

	return (
		<div className={className}>
			{renderMIPCreateSectionHeaderPortal(
				<Flex direction="column" gap="1rem">
					<Text size="B4Bold">{tMipCreate('kyc.header.title')}</Text>
					<Text size="B6Medium" c="dark.0">
						{tMipCreate('kyc.header.subtitle_new')}
					</Text>
				</Flex>
			)}
			<Button
				onClick={() => setShowHowToProvideDocsModal(true)}
				leftSection={
					<Icon variant="open-book" size="vlg" style={{ marginRight: '0.3rem' }} />
				}
				variant="link-color"
				mt="-1.5rem"
				mb="2rem"
			>
				<Text size="B6Bold" lh="normal">
					{tMipCreate('kyc.header.preview_instructions_sent_to_investors')}
				</Text>
			</Button>
			<SentryRoutes>
				<Route
					path={MIP_CREATE_SUB_ROUTES.KYC.KYC_DOCUMENTS}
					element={
						<KYCDocuments
							proofs={proofs}
							certification={certification}
							onProofChange={onProofChange}
							onCertificationChange={onCertificationChange}
							isSaving={isSaving}
						/>
					}
				/>
				<Route
					path={MIP_CREATE_SUB_ROUTES.KYC.KYC_DECLARATIONS}
					element={
						<NoSaveStep>
							<KYCDeclarations />
						</NoSaveStep>
					}
				/>

				<Route
					path="*"
					element={
						<Navigate
							to={`${mipCreateRoute}${MIP_CREATE_ROUTES.KYC}${MIP_CREATE_SUB_ROUTES.KYC.KYC_DOCUMENTS}`}
						/>
					}
				/>
			</SentryRoutes>
			{showHowToProvideDocsModal && (
				<HowTopProvideDocsModal
					requestCertifiedCopiesOfDocuments={isAnyCertificationRequired}
					onClose={() => setShowHowToProvideDocsModal(false)}
				/>
			)}
		</div>
	);
};

const Styled = styled(KYCRequirements)`
	${BorderedBox} {
		margin-bottom: 0.5rem;
	}
`;

export { Styled as KYCRequirements };
